/**
 * Всё, что определяет итоговый аудиоклип, входит в хэш.
 * Изменили DSP-цепочку, модель или нормализацию → увеличьте ENGINE_VERSION:
 * новые URL, старые клипы естественно вытесняются из кэшей.
 */
export const ENGINE_VERSION = "piper1-dsp1";

export const PRESETS = ["calm", "deep", "natural"] as const;
export type PresetId = (typeof PRESETS)[number];

export const PRESET_LABELS: Record<PresetId, string> = {
  calm: "Спокойный",
  deep: "Глубокий",
  natural: "Естественный",
};

export interface VoiceInfo {
  id: string;
  name: string;
  language: string;
  gender: "f" | "m";
  /** loading — модель голоса ещё загружается на сервере; выбрать его пока нельзя */
  status?: "ready" | "loading";
  engine?: string;
}

/**
 * Голос по умолчанию и запасной каталог на случай, если TTS-сервис недоступен.
 * Настоящий список голосов (имена, пол) отдаёт TTS-сервис — API пробрасывает его в GET /v1/config.
 */
export const VOICES: VoiceInfo[] = [
  { id: "ru-irinia-medium", name: "Ирина", language: "ru", gender: "f", engine: "piper" },
  // Vosk: показываем «загружается», пока сервер прогревает модель (женские дикторы 0–2)
  { id: "vosk-ru-0", name: "Вера", language: "ru", gender: "f", engine: "vosk" },
  { id: "vosk-ru-1", name: "Мила", language: "ru", gender: "f", engine: "vosk" },
  { id: "vosk-ru-2", name: "Ника", language: "ru", gender: "f", engine: "vosk" },
];

/** Личный голос пользователя (клон): id вида user-<uuid профиля> */
export const USER_VOICE_PREFIX = "user-";
export const isUserVoice = (id: string) => id.startsWith(USER_VOICE_PREFIX);
export const DEFAULT_VOICE = VOICES[0]!.id;
export const DEFAULT_PRESET: PresetId = "calm";

export const MAX_AFFIRMATION_LEN = 300;

const WS = /\s+/gu;

/** Каноническая форма текста для синтеза и хэша. Одинаковая на клиенте и сервере. */
export function normalizeForTts(text: string): string {
  let t = text.normalize("NFC").replace(WS, " ").trim();
  t = t.replace(/\.{3,}/g, "…").replace(/[«»„“”"]/g, "");
  if (!t) return t;
  t = t[0]!.toLocaleUpperCase("ru") + t.slice(1);
  if (!/[.!?…]$/u.test(t)) t += ".";
  return t;
}

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  return toHex(await globalThis.crypto.subtle.digest("SHA-256", data));
}

export function ttsKeyMaterial(text: string, voiceId: string, preset: PresetId, engine = ENGINE_VERSION): string {
  return `${engine}|${voiceId}|${preset}|${normalizeForTts(text)}`;
}

export function ttsHash(text: string, voiceId: string, preset: PresetId, engine = ENGINE_VERSION): Promise<string> {
  return sha256Hex(ttsKeyMaterial(text, voiceId, preset, engine));
}

export const HASH_RE = /^[0-9a-f]{64}$/;

export function audioPath(hash: string): string {
  return `/audio/${hash}.mp3`;
}

export function storageKey(hash: string): string {
  return `tts/v1/${hash.slice(0, 2)}/${hash}.mp3`;
}
